

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Button, Combobox, ConfirmDialog, DiscardChangesDialog, NumberInput, useToast } from "@a2z/ui";
import type { PaymentReviewRejectReasonCode, SellerOrderDetail } from "@/lib/api/types";
import { formatAmountValue } from "./order-list.utils";
import { PaymentReviewProofViewer } from "./PaymentReviewProofViewer";
import { usePaymentReviewState } from "./usePaymentReviewState";

type Props = {
  locale: string;
  storeId: string;
  orderId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (order: SellerOrderDetail) => void;
  openOrderHref?: string;
};

function mapErrorMessage(
  code: string | null,
  t: ReturnType<typeof useTranslations>,
): string | null {
  if (!code) {
    return null;
  }
  if (code === "STALE_STATUS") {
    return t(keys.orders.paymentReviewConflictNotice);
  }
  if (code === "PAYMENT_PROOF_REQUIRED") {
    return t(keys.orders.paymentReviewProofRequired);
  }
  if (code === "INVALID_APPROVED_AMOUNT") {
    return t(keys.orders.approvedAmountInvalid);
  }
  if (code === "PAYMENT_REJECT_REASON_REQUIRED") {
    return t(keys.orders.reviewRejectReasonRequired);
  }
  if (code === "PAYMENT_REVIEW_LOAD_ERROR") {
    return t(keys.orders.paymentReviewLoadError);
  }
  return t(keys.errors.generic);
}

function getDifferenceMeta(
  difference: number,
  t: ReturnType<typeof useTranslations>,
) {
  if (difference < 0) {
    return t(keys.orders.differenceUnderpaid);
  }
  if (difference > 0) {
    return t(keys.orders.differenceOverpaid);
  }
  return t(keys.orders.differenceExact);
}

export function PaymentReviewDialog({
  locale,
  storeId,
  orderId,
  isOpen,
  onClose,
  onSuccess,
  openOrderHref,
}: Props) {
  const t = useTranslations();
  const toast = useToast();
  const [lastSubmittedAction, setLastSubmittedAction] = useState<"approve" | "reject">(
    "approve",
  );
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const touchStartY = useRef<number | null>(null);

  const state = usePaymentReviewState({
    isOpen,
    storeId,
    orderId,
    onSuccess: (updatedOrder) => {
      toast.success(
        lastSubmittedAction === "reject"
          ? t(keys.orders.paymentRejectedNotice)
          : t(keys.orders.paymentApprovedNotice),
      );
      onSuccess(updatedOrder);
      setShowApproveConfirm(false);
      setShowRejectConfirm(false);
      onClose();
    },
  });

  // Reset local state on dialog open/close (adjust-state-during-render
  // pattern instead of an effect).
  const isActive = Boolean(isOpen && orderId);
  const [wasActive, setWasActive] = useState(isActive);
  if (wasActive !== isActive) {
    setWasActive(isActive);
    if (!isActive) {
      setLastSubmittedAction("approve");
      setShowApproveConfirm(false);
      setShowRejectConfirm(false);
      setShowDiscardConfirm(false);
    }
  }

  const requestClose = () => {
    if (state.isSubmitting) {
      return;
    }
    if (state.hasUnsavedChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  useEffect(() => {
    if (!isOpen || !orderId) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, state.hasUnsavedChanges, state.isSubmitting]);

  const difference = state.buyerEnteredAmount - state.requiredAmount;
  const differenceLabel = getDifferenceMeta(difference, t);
  const errorMessage = mapErrorMessage(state.errorCode, t);

  const rejectOptions = useMemo(
    () =>
      [
        ["amount_mismatch", keys.orders.reviewRejectAmountMismatch],
        ["proof_unclear", keys.orders.reviewRejectProofUnclear],
        ["wrong_proof", keys.orders.reviewRejectWrongProof],
        ["suspected_fake", keys.orders.reviewRejectSuspectedFake],
        ["other", keys.orders.reviewRejectOther],
      ] as Array<[PaymentReviewRejectReasonCode, string]>,
    [],
  );
  const rejectReasonLabelByCode = useMemo(
    () =>
      Object.fromEntries(rejectOptions) as Record<PaymentReviewRejectReasonCode, string>,
    [rejectOptions],
  );

  if (!isOpen || !orderId) {
    return null;
  }

  const handleApprove = () => {
    setLastSubmittedAction("approve");
    if (!state.hasProof) {
      state.setErrorCode("PAYMENT_PROOF_REQUIRED");
      return;
    }
    if (!state.hasValidApprovedAmount) {
      state.setErrorCode("INVALID_APPROVED_AMOUNT");
      return;
    }
    setShowApproveConfirm(true);
  };

  const handleReject = () => {
    setLastSubmittedAction("reject");
    if (!state.reasonCode) {
      state.setErrorCode("PAYMENT_REJECT_REASON_REQUIRED");
      return;
    }
    setShowRejectConfirm(true);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={requestClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center md:p-4">
        <div
          role="dialog"
          aria-modal="true"
          onTouchStart={(event) => {
            touchStartY.current = event.changedTouches[0]?.clientY ?? null;
          }}
          onTouchEnd={(event) => {
            const startY = touchStartY.current;
            const endY = event.changedTouches[0]?.clientY ?? null;
            if (startY !== null && endY !== null && endY - startY > 80) {
              requestClose();
            }
            touchStartY.current = null;
          }}
          className="flex h-[92vh] w-full flex-col rounded-t-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-lg)] md:h-auto md:max-h-[90vh] md:max-w-4xl md:rounded-2xl"
        >
          <div className="flex items-center justify-between border-b border-[color:var(--border-light)] px-4 py-3 sm:px-5">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-[color:var(--text-primary)]">
                {t(keys.orders.reviewPaymentTitle)}
              </h3>
              {state.order ? (
                <p className="truncate text-xs text-[color:var(--text-muted)]">
                  {state.order.customer.name} • {state.order.orderCode}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={requestClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[color:var(--border-light)] text-[color:var(--text-secondary)]"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
            {state.isConflict ? (
              <div className="mb-3 rounded-lg border border-[color:var(--warning-500)]/35 bg-[color:var(--warning-500)]/10 px-3 py-2 text-sm text-[color:var(--text-primary)]">
                {t(keys.orders.paymentReviewConflictNotice)}{" "}
                <span className="text-[color:var(--text-muted)]">
                  {t(keys.orders.paymentReviewOpenOrderHint)}
                </span>
              </div>
            ) : null}
            {errorMessage ? (
              <div className="mb-3 rounded-lg border border-[color:var(--error-500)]/35 bg-[color:var(--error-500)]/10 px-3 py-2 text-sm text-[color:var(--error-500)]">
                {errorMessage}
              </div>
            ) : null}

            {state.isLoading ? (
              <p className="text-sm text-[color:var(--text-muted)]">{t(keys.common.loading)}</p>
            ) : state.order ? (
              <div className="space-y-4">
                <section className="rounded-xl border border-[color:var(--border-light)] p-3 text-sm">
                  <div className="flex items-center justify-between py-1">
                    <span className="text-[color:var(--text-muted)]">{t(keys.orders.requiredAmountLabel)}</span>
                    <bdi>{formatAmountValue(locale, state.requiredAmount)}</bdi>
                  </div>
                  <div className="flex items-center justify-between py-1">
                    <span className="text-[color:var(--text-muted)]">{t(keys.orders.buyerEnteredAmountLabel)}</span>
                    <bdi>{formatAmountValue(locale, state.buyerEnteredAmount)}</bdi>
                  </div>
                  <div className="flex items-center justify-between py-1 font-semibold text-[color:var(--text-primary)]">
                    <span>{t(keys.orders.differenceAmountLabel)}</span>
                    <bdi>
                      {formatAmountValue(locale, difference)} ({differenceLabel})
                    </bdi>
                  </div>
                  <label className="mt-3 flex flex-col gap-1 text-xs text-[color:var(--text-muted)]">
                    {t(keys.orders.approvedAmountLabel)}
                    <NumberInput
                      name="approvedAmount"
                      min={0}
                      allowDecimal
                      value={state.approvedAmountInput}
                      onChange={(event) => state.setApprovedAmountInput(event.target.value)}
                      disabled={state.isConflict}
                    />
                  </label>
                </section>

                <section className="space-y-2">
                  <PaymentReviewProofViewer
                    locale={locale}
                    proofItems={state.proofItems}
                    selectedIndex={state.selectedProofIndex}
                    onSelect={state.setSelectedProofIndex}
                  />
                </section>

                <section className="space-y-2 rounded-xl border border-[color:var(--border-light)] p-3">
                  <div className="flex flex-col gap-1 text-xs text-[color:var(--text-muted)]">
                    {t(keys.orders.reviewRejectReasonLabel)}
                    <Combobox
                      options={[
                        { value: "", label: t(keys.orders.declineReasonPlaceholder) },
                        ...rejectOptions.map(([value, labelKey]) => ({
                          value,
                          label: t(labelKey),
                        })),
                      ]}
                      value={state.reasonCode}
                      onChange={(value) =>
                        state.setReasonCode((value ?? "") as PaymentReviewRejectReasonCode | "")
                      }
                    />
                  </div>

                  <label className="flex flex-col gap-1 text-xs text-[color:var(--text-muted)]">
                    {t(keys.orders.declineNoteLabel)}
                    <textarea
                      value={state.note}
                      onChange={(event) => state.setNote(event.target.value)}
                      className="min-h-20 rounded-lg border border-[color:var(--border-light)] px-3 py-2 text-sm text-[color:var(--text-primary)]"
                      disabled={state.isConflict}
                    />
                  </label>
                </section>
              </div>
            ) : null}
          </div>

          <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5">
            <Button
              type="button"
              variant="primary"
              className="h-10 rounded-lg px-4 text-sm font-semibold"
              onClick={handleApprove}
              disabled={
                state.isSubmitting ||
                state.isLoading ||
                state.isConflict ||
                !state.hasProof
              }
            >
              {t(keys.orders.approvePaymentAction)}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-lg px-4 text-sm font-semibold"
              onClick={handleReject}
              disabled={state.isSubmitting || state.isLoading || state.isConflict}
            >
              {t(keys.orders.rejectPaymentAction)}
            </Button>
            {openOrderHref ? (
              <a
                href={openOrderHref}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-[color:var(--border-light)] px-4 text-sm font-semibold text-[color:var(--text-secondary)]"
              >
                {t(keys.orders.openOrderAction)}
              </a>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              className="ms-auto h-10 rounded-lg px-4 text-sm font-semibold"
              onClick={requestClose}
              disabled={state.isSubmitting}
            >
              {t(keys.common.close)}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showApproveConfirm}
        elevated
        title={t(keys.orders.confirmApproveTitle)}
        body={t(keys.orders.confirmApproveBody, {
          required: formatAmountValue(locale, state.requiredAmount),
          buyer: formatAmountValue(locale, state.buyerEnteredAmount),
          approved: formatAmountValue(locale, state.approvedAmount || 0),
        })}
        confirmLabel={t(keys.common.submit)}
        cancelLabel={t(keys.common.back)}
        isSubmitting={state.isSubmitting}
        onCancel={() => setShowApproveConfirm(false)}
        onConfirm={() => {
          setShowApproveConfirm(false);
          void state.submitApprove();
        }}
      />

      <ConfirmDialog
        isOpen={showRejectConfirm}
        elevated
        title={t(keys.orders.confirmRejectTitle)}
        body={t(keys.orders.confirmRejectBody, {
          reason:
            state.reasonCode && rejectReasonLabelByCode[state.reasonCode]
              ? t(rejectReasonLabelByCode[state.reasonCode])
              : t(keys.common.empty),
        })}
        confirmLabel={t(keys.common.submit)}
        cancelLabel={t(keys.common.back)}
        isSubmitting={state.isSubmitting}
        onCancel={() => setShowRejectConfirm(false)}
        onConfirm={() => {
          setShowRejectConfirm(false);
          void state.submitReject();
        }}
      />

      <DiscardChangesDialog
        isOpen={showDiscardConfirm}
        elevated
        onContinue={() => setShowDiscardConfirm(false)}
        onDiscard={() => {
          setShowDiscardConfirm(false);
          onClose();
        }}
        title={t(keys.forms.discardChangesTitle)}
        body={t(keys.forms.discardChangesBody)}
        discardLabel={t(keys.forms.discardChangesDiscard)}
        continueLabel={t(keys.forms.discardChangesContinue)}
      />
    </>
  );
}
